// Type-only on purpose: jszip is ~95 KB and only two signatures below need its type. Dropping
// `type` compiles and passes tests while silently returning the whole package to the entry
// chunk. The runtime constructor is imported dynamically in `downloadFilesAsZip`.
import type JSZip from 'jszip';
import readXlsxFile from 'read-excel-file';

export interface IFile {
  fileName: string;
  file: Blob;
}

export function createBlobFromText(text: string) {
  return new Blob([text]);
}

export function downloadText(text: string, fileName: string) {
  return downloadFile({
    file: createBlobFromText(text),
    fileName,
  });
}

export function downloadFile({ file, fileName }: IFile) {
  const link = createDownloadLinkDOM({ file, fileName });

  return new Promise((resolve, reject) => {
    link.onclick = () => resolve(link.parentNode!.removeChild(link));
    link.onerror = reject;

    link.click();
  });
}

export function createDownloadLinkDOM({ file, fileName }: IFile) {
  const url = window.URL.createObjectURL(file);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', fileName);

  document.body.appendChild(link);

  return link;
}

export function downloadZipFile(zipper: JSZip, zipFileName: string) {
  return zipper
    .generateAsync({ type: 'blob' })
    .then((zipperBlob) => downloadFile({ file: zipperBlob, fileName: `${zipFileName}.zip` }));
}

export function addFileToZipper(zipper: JSZip, { file, fileName }: IFile) {
  return zipper.file(fileName, file, { base64: true });
}

export async function downloadFilesAsZip(files: IFile[], zipFileName: string) {
  const { default: JSZipCtor } = await import(/* webpackChunkName: 'jszip' */ 'jszip');
  const zipper = new JSZipCtor();
  const wrapper = zipper.folder(zipFileName);

  if (!wrapper) {
    return null;
  }

  files.forEach((file) => addFileToZipper(wrapper, file));
  return downloadZipFile(zipper, zipFileName);
}

export function readFile(file: File, onload: (reader: FileReader) => () => void) {
  const reader = new FileReader();
  reader.onload = onload(reader);
  reader.readAsText(file);
}

async function convertXlsxToCsv(xlsxFile: File) {
  const result: string[][] = await readXlsxFile(xlsxFile);
  const rowsJoined = result.map((rows) =>
    rows.map((row) => (typeof row === 'string' && row?.includes(',') ? `"${row}"` : row)).join(',')
  );
  return rowsJoined.join('\r\n');
}

export function convertBlobToUrl(data: Blob): string {
  return window.URL.createObjectURL(data);
}

export function downloadUrl(url: string, fileName: string) {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
}

export function readFileAsDataUrl(file: File | Blob) {
  const reader = new FileReader();
  return new Promise(function (resolve) {
    reader.onload = function (event) {
      resolve(event.target?.result);
    };
    reader.readAsDataURL(file);
  });
}

export async function downloadBlobFromUrl(url: string) {
  const res = await fetch(url);
  return res.blob();
}

export async function downloadUrlsAsZip(
  urls: string[],
  zipFileName: string,
  getFileName: (url: string, index: number) => string
) {
  const files = urls.map(async (url, index) => {
    const fileName = getFileName(url, index);
    const blob = await downloadBlobFromUrl(url);
    return { fileName, file: blob };
  });

  return downloadFilesAsZip(await Promise.all(files), zipFileName);
}

export function getDataUrlFromUrl(url: RequestInfo) {
  return fetch(url, { mode: 'cors', cache: 'no-cache', headers: { origin: window.location.origin } })
    .then((response) => response.blob())
    .then((blob) => {
      return readFileAsDataUrl(blob);
    });
}
export const convertSvgToPng = async (svgUrl: string): Promise<string | null> => {
  try {
    // Started together: the chunk request does not depend on the SVG, so awaiting it first
    // would make the first conversion pay two serial round trips instead of one.
    const [{ Canvg }, response] = await Promise.all([import(/* webpackChunkName: 'canvg' */ 'canvg'), fetch(svgUrl)]);
    const svgText = await response.text();

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    const canvgInstance = await Canvg.from(ctx, svgText);
    await canvgInstance.render();

    return canvas.toDataURL('image/png');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error converting SVG to PNG:', error);
    return null;
  }
};

export const scaleImageToWidth = (pngDataUrl: string, desiredWidth: number) => {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const img = new Image();
    img.src = pngDataUrl;

    img.onload = () => {
      const originalWidth = img.width;
      const originalHeight = img.height;

      // Calculate the new height while maintaining the aspect ratio
      const aspectRatio = originalHeight / originalWidth;
      const newHeight = desiredWidth * aspectRatio;

      resolve({ width: desiredWidth, height: newHeight });
    };

    img.onerror = (error) => reject(error);
  });
};

export async function convertSvgFileToPng(svgFile: File): Promise<File> {
  const svgText = await svgFile.text();
  const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(svgBlob);

  const img = new Image();
  img.src = url;

  return new Promise((resolve, reject) => {
    img.onload = () => {
      URL.revokeObjectURL(url);

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to convert SVG to image'));
          return;
        }

        const convertedFile = new File([blob], `${svgFile.name.split('.')[0]}.png`, { type: 'image/png' });
        resolve(convertedFile);
      }, 'image/png');
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG image'));
    };
  });
}

/**
 * Replaces characters that are forbidden in Windows filenames with `replaceValue` (default `'_'`).
 * Filtered characters:
 *   < > : " / \ | ? *  — explicitly disallowed by Windows
 *   \x00–\x1F          — ASCII control characters (also disallowed by Windows)
 */
export function sanitizeFilename(name: string, replaceValue = '_') {
  // eslint-disable-next-line no-control-regex
  return name.replace(/[<>:"/\\|?*\x00-\x1F]/g, replaceValue);
}
export function getExifOrientation(buffer: ArrayBuffer): number {
  const view = new DataView(buffer);

  if (view.byteLength < 2 || view.getUint16(0, false) !== 0xffd8) {
    return -2;
  }

  const length = view.byteLength;
  let offset = 2;

  while (offset < length) {
    if (offset + 2 > length) {
      break;
    }
    const marker = view.getUint16(offset, false);
    offset += 2;

    if (marker === 0xffe1) {
      if (offset + 8 > length || view.getUint32(offset + 2, false) !== 0x45786966) {
        return -1;
      }

      const tiffOffset = offset + 8;
      const little = view.getUint16(tiffOffset, false) === 0x4949;
      const firstIfdOffset = view.getUint32(tiffOffset + 4, little);
      let dirOffset = tiffOffset + firstIfdOffset;

      if (dirOffset + 2 > length) {
        return -1;
      }
      const tagCount = view.getUint16(dirOffset, little);
      dirOffset += 2;

      for (let i = 0; i < tagCount; i++) {
        const entryOffset = dirOffset + i * 12;
        if (entryOffset + 12 > length) {
          break;
        }
        if (view.getUint16(entryOffset, little) === 0x0112) {
          return view.getUint16(entryOffset + 8, little);
        }
      }
      return -1;
    } else if (marker === 0xffda || marker === 0xffd9) {
      break;
    } else if ((marker & 0xff00) !== 0xff00) {
      break;
    } else {
      if (offset + 2 > length) {
        break;
      }
      const segmentLength = view.getUint16(offset, false);
      if (segmentLength < 2) {
        break;
      }
      offset += segmentLength;
    }
  }

  return -1;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

export async function correctImageOrientation(file: File): Promise<File> {
  if (!/^image\/jpe?g$/i.test(file.type)) {
    return file;
  }

  try {
    const buffer = await file.arrayBuffer();
    const orientation = getExifOrientation(buffer);

    if (orientation <= 1) {
      return file;
    }

    const objectUrl = URL.createObjectURL(file);
    let img: HTMLImageElement;
    try {
      img = await loadImage(objectUrl);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return file;
    }

    ctx.drawImage(img, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) {
      return file;
    }

    return new File([blob], file.name, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  }
}

export default {
  convertXlsxToCsv,
  downloadFile,
  downloadZipFile,
  readFile,
  readFileAsDataUrl,
  getDataUrlFromUrl,
  convertSvgToPng,
  convertSvgFileToPng,
  scaleImageToWidth,
  correctImageOrientation,
};
